import { HEALTH_STATUS_OPTIONS } from '../constants';
import { FilterTag, FilterTagType, SelectionType, SubsystemClassificationItem } from '../search.types';
import { createFilterTag } from './filterFactory';

type SearchFilterTypeLabels = Partial<Record<FilterTagType | SelectionType, string>>;

const HEALTH_STATUS_LABELS: Record<string, string> = Object.fromEntries(
  HEALTH_STATUS_OPTIONS.map((opt) => [opt.value, opt.label])
);

interface ParseSearchResult {
  filters: FilterTag[];
  searchValue: string;
}

const DEFAULT_FILTER_LABELS: SearchFilterTypeLabels = {
  search: 'Search',
  regex: 'RegEx',
  ip: 'IP address',
  mac: 'MAC address',
  wwn: 'WWN',
  volid: 'Volume ID',
  health_status: 'Health status',
  tag: 'Tag',
  technology: 'Technology',
  tag_category: 'Tag Category',
};

const KNOWN_QUERY_KEYS = new Set([
  'search',
  'regex',
  'ip',
  'mac',
  'wwn',
  'volid',
  'health_status',
  'tag',
  'find',
  'class',
  'hw_type',
  'subsystem',
]);

const stripLeadingQuestionMark = (query: string): string => {
  return query.startsWith('?') ? query.slice(1) : query;
};

const getQueryKey = (part: string): string => {
  const equalIndex = part.indexOf('=');
  return equalIndex >= 0 ? part.slice(0, equalIndex) : part;
};

const getQueryValue = (part: string): string => {
  const equalIndex = part.indexOf('=');
  return equalIndex >= 0 ? part.slice(equalIndex + 1) : '';
};

const isSerializedSearchQuery = (query: string): boolean => {
  const normalizedQuery = stripLeadingQuestionMark(query).trim();
  if (normalizedQuery.length === 0 || !normalizedQuery.includes('=')) return false;

  return normalizedQuery
    .split('&')
    .filter(Boolean)
    .some((part) => KNOWN_QUERY_KEYS.has(getQueryKey(part)));
};

const encodeQueryValueCompat = (value: string): string => {
  return encodeURIComponent(value)
    .replaceAll(/[!'()*]/g, (char) => {
      return `%${char.charCodeAt(0).toString(16).toUpperCase()}`;
    })
    .replaceAll(/%3A/gi, ':');
};

const decodeQueryValueCompat = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const appendQueryPart = (parts: string[], key: string, value: string, trim = true): void => {
  const normalized = trim ? value.trim() : value;
  if (!normalized) return;
  parts.push(`${key}=${encodeQueryValueCompat(normalized)}`);
};

const canonicalizeSerializedQuery = (
  query: string,
  options: { normalizeFindToSearch: boolean; }
): string => {
  const normalizedQuery = stripLeadingQuestionMark(query).trim();
  if (!normalizedQuery || !isSerializedSearchQuery(normalizedQuery)) {
    return '';
  }

  const parts = normalizedQuery.split('&').filter(Boolean);
  const hasSearch = parts.some((part) => getQueryKey(part) === 'search');
  const result: string[] = [];

  for (const part of parts) {
    if (!part.includes('=')) {
      result.push(part);
      continue;
    }

    const key = getQueryKey(part);
    if (!KNOWN_QUERY_KEYS.has(key)) {
      result.push(part);
      continue;
    }

    const value = decodeQueryValueCompat(getQueryValue(part));

    if (key === 'find') {
      if (!options.normalizeFindToSearch) {
        appendQueryPart(result, 'find', value, false);
      } else if (!hasSearch && value) {
        appendQueryPart(result, 'search', value, false);
      }
      continue;
    }

    appendQueryPart(result, key, value, false);
  }

  return result.join('&');
};

// Fix: pure function, returns new array
const pushTagFilter = (filters: FilterTag[], value: string): FilterTag[] => {
  if (value.includes(':') && value.split(':').length === 2) {
    return [
      ...filters,
      createFilterTag('tag_category_tag', value, { typeLabels: DEFAULT_FILTER_LABELS }),
    ];
  }

  const existingIndex = filters.findIndex((filter) => filter.type === 'tag');
  if (existingIndex >= 0) {
    return filters.map((filter, index) =>
      index === existingIndex
        ? { ...filter, value: `${filter.value}, ${value}` }
        : filter
    );
  }

  return [...filters, createFilterTag('tag', value, { typeLabels: DEFAULT_FILTER_LABELS })];
};

export const parseSearchQueryString = (
  query: string,
  availableArchitecture: SubsystemClassificationItem[] = []
): ParseSearchResult => {
  const normalizedQuery = stripLeadingQuestionMark(query);
  if (!isSerializedSearchQuery(normalizedQuery)) {
    return { filters: [], searchValue: normalizedQuery };
  }

  const params = new URLSearchParams(normalizedQuery);
  let filters: FilterTag[] = [];
  let searchValue = '';
  const hasSearchParam = params.has('search');

  for (const [key, value] of params.entries()) {
    switch (key) {
      case 'search':
      case 'regex':
      case 'ip':
      case 'mac':
      case 'wwn':
      case 'volid':
      case 'health_status':
        filters = [
          ...filters,
          createFilterTag(key, value, { availableArchitecture, typeLabels: DEFAULT_FILTER_LABELS }),
        ];
        break;
      case 'tag':
        filters = pushTagFilter(filters, value);
        break;
      case 'find':
        if (value.startsWith('tag:') && value.split(':').length === 3) {
          const [, category, tagValue] = value.split(':');
          filters = pushTagFilter(filters, `${category}:${tagValue}`);
        } else if (!hasSearchParam && !searchValue) {
          searchValue = value;
        }
        break;
      case 'subsystem': {
        const classValue = params.get('class');
        if (classValue) {
          filters = [
            ...filters,
            createFilterTag('class_subsystem', `${classValue}:${value}`, {
              availableArchitecture,
              typeLabels: DEFAULT_FILTER_LABELS,
            }),
          ];
          break;
        }
        const hwTypeValue = params.get('hw_type');
        if (hwTypeValue) {
          filters = [
            ...filters,
            createFilterTag('hw_type_subsystem', `${hwTypeValue}:${value}`, {
              availableArchitecture,
              typeLabels: DEFAULT_FILTER_LABELS,
            }),
          ];
        }
        break;
      }
      case 'class':
      case 'hw_type':
        break;
      default:
        break;
    }
  }

  return { filters, searchValue };
};

export const serializeSearchQuery = (
  filters: FilterTag[],
  freeText: string
): string => {
  const parts: string[] = [];
  let searchValue = '';

  filters.forEach((filter) => {
    switch (filter.type) {
      case 'search':
        searchValue = filter.value.trim();
        break;
      case 'tag':
        filter.value.split(',').forEach((tag) => {
          const normalizedTag = tag.trim();
          if (normalizedTag) appendQueryPart(parts, 'tag', normalizedTag);
        });
        break;
      case 'tag_category_tag':
        appendQueryPart(parts, 'tag', filter.value);
        break;
      case 'class_subsystem': {
        const [classValue, subsystemValue] = filter.value.split(':');
        if (classValue?.trim()) appendQueryPart(parts, 'class', classValue);
        if (subsystemValue?.trim()) appendQueryPart(parts, 'subsystem', subsystemValue);
        break;
      }
      case 'hw_type_subsystem': {
        const [hwTypeValue, subsystemValue] = filter.value.split(':');
        if (hwTypeValue?.trim()) appendQueryPart(parts, 'hw_type', hwTypeValue);
        if (subsystemValue?.trim()) appendQueryPart(parts, 'subsystem', subsystemValue);
        break;
      }
      default:
        appendQueryPart(parts, filter.type, filter.value, false);
        break;
    }
  });

  const normalizedFreeText = freeText.trim();
  if (normalizedFreeText) {
    searchValue = normalizedFreeText;
  }
  if (searchValue) {
    appendQueryPart(parts, 'search', searchValue);
  }

  return parts.join('&');
};

export const normalizeSearchQueryForApi = (rawQuery: string): string => {
  return canonicalizeSerializedQuery(rawQuery, { normalizeFindToSearch: true });
};

export const buildSearchRouteQuery = (rawQuery: string): string => {
  const normalizedQuery = stripLeadingQuestionMark(rawQuery).trim();
  if (!normalizedQuery) return '';
  if (isSerializedSearchQuery(normalizedQuery)) {
    return canonicalizeSerializedQuery(normalizedQuery, { normalizeFindToSearch: false });
  }
  return `find=${encodeQueryValueCompat(normalizedQuery)}`;
};

export const formatSearchQueryTitle = (
  query: string,
  availableArchitecture: SubsystemClassificationItem[] = []
): string => {
  const normalizedQuery = stripLeadingQuestionMark(query);
  if (!isSerializedSearchQuery(normalizedQuery)) return normalizedQuery;

  const { filters, searchValue } = parseSearchQueryString(normalizedQuery, availableArchitecture);
  if (filters.length === 0) {
    return searchValue || normalizedQuery;
  }

  const parts = filters.map((filter) => {
    switch (filter.type) {
      case 'search':
        return `Search: ${filter.value}`;
      case 'tag':
        return `Tags: ${filter.value}`;
      case 'regex':
        return `RegEx: ${filter.value}`;
      case 'ip':
        return `IP: ${filter.value}`;
      case 'mac':
        return `MAC: ${filter.value}`;
      case 'wwn':
        return `WWN: ${filter.value}`;
      case 'volid':
        return `Volume ID: ${filter.value}`;
      case 'health_status': {
        const healthLabel = HEALTH_STATUS_LABELS[filter.value] ?? filter.value;
        return `Health status: ${healthLabel}`;
      }
      case 'class_subsystem':
      case 'hw_type_subsystem':
      case 'tag_category_tag':
        return filter.label;
      default:
        return `${filter.label}: ${filter.value}`;
    }
  });

  return parts.join(' | ');
};
